import { RentalAssetSummary, RentalBillingUnit } from '../types/rental.type'

export interface BillingPeriod {
  interval: number
  unit: RentalBillingUnit
}

/** What each legacy cycle name means as a period, mirroring the server's own table. */
const CYCLE_PERIODS: Record<string, BillingPeriod> = {
  hourly: { interval: 1, unit: 'hour' },
  daily: { interval: 1, unit: 'day' },
  weekly: { interval: 1, unit: 'week' },
  monthly: { interval: 1, unit: 'month' },
  quarterly: { interval: 3, unit: 'month' },
  semi_annually: { interval: 6, unit: 'month' },
  annually: { interval: 12, unit: 'month' },
}

const DEFAULT_PERIOD: BillingPeriod = { interval: 1, unit: 'month' }

/**
 * The period an item's rate is quoted in.
 *
 * Newer servers resolve this and send `defaultBillingInterval`/`defaultBillingUnit`, but
 * assets configured through the legacy `defaultBillingCycle` alone come back with those
 * two null, so the same precedence is applied here: stored period first, then the cycle,
 * then a month. Guessing a different order than the server would quote the customer a
 * period they are not billed in — and reading the raw unit renders "per null".
 */
export function resolveAssetBillingPeriod(
  asset: Pick<
    RentalAssetSummary,
    'defaultBillingInterval' | 'defaultBillingUnit' | 'defaultBillingCycle'
  > | null
  | undefined,
): BillingPeriod {
  if (asset?.defaultBillingUnit) {
    const interval = Number(asset.defaultBillingInterval)
    return {
      interval: Number.isFinite(interval) && interval >= 1 ? Math.floor(interval) : 1,
      unit: asset.defaultBillingUnit,
    }
  }

  return CYCLE_PERIODS[asset?.defaultBillingCycle || ''] || DEFAULT_PERIOD
}
